import { useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import Browse from './pages/Browse';
import Skill from './pages/Skill';
import Submit from './pages/Submit';
import Profile from './pages/Profile';
import Docs from './pages/Docs';
import Evaluate from './pages/Evaluate';
import Login from './pages/Login';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import NotFound from './pages/NotFound';

function AuthCallback() {
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Cookie was set by the server on redirect — just verify auth, then go to stored redirect or home
    login().then(() => {
      const stored = sessionStorage.getItem('postLoginRedirect');
      sessionStorage.removeItem('postLoginRedirect');
      navigate(stored || '/', { replace: true });
    }).catch(() => {
      navigate('/login', { replace: true });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="pt-24 pb-16 min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-brand-500/30 border-t-brand-500 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-400">Authenticating...</p>
      </div>
    </div>
  );
}

// Fixed set of error messages — never reflect user-controlled text
const ERROR_MESSAGES: Record<string, string> = {
  'No+authorization+code+provided': 'No authorization code was provided by GitHub.',
  'Invalid+OAuth+state+parameter': 'OAuth verification failed. Please try again.',
  'Authentication+failed.+Please+try+again.': 'Authentication failed. Please try again.',
};

function AuthError() {
  return (
    <div className="pt-24 pb-16 min-h-screen flex items-center justify-center">
      <div className="glass-card p-10 max-w-md text-center">
        <div className="w-14 h-14 rounded-2xl bg-red-500/10 mx-auto mb-4 flex items-center justify-center text-red-400 text-2xl">
          !
        </div>
        <h2 className="text-xl font-bold text-white mb-3">Authentication Error</h2>
        <p className="text-slate-400 mb-6">Authentication failed. Please try again.</p>
        <a href="/login" className="btn-gradient text-sm px-6 py-3 rounded-xl inline-block">
          <span>Try Again</span>
        </a>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <div className="min-h-screen bg-surface flex flex-col">
        <Header />
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/browse" element={<Browse />} />
            <Route path="/s/:owner/:skill" element={<Skill />} />
            <Route path="/submit" element={<Submit />} />
            <Route path="/u/:username" element={<Profile />} />
            <Route path="/docs" element={<Docs />} />
            <Route path="/evaluate" element={<Evaluate />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/error" element={<AuthError />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
        <Toaster
          position="bottom-right"
          toastOptions={{
            className: 'toast-custom',
            duration: 3000,
            style: {
              background: 'rgba(17, 17, 17, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: 'white',
              borderRadius: '12px',
            },
          }}
        />
      </div>
    </AuthProvider>
  );
}

export default App;
